import React, { useCallback, useState } from 'react';
import './Styles.css';
import { formatMessage } from '../../Localization/formatMessage';
import Messages from '../../Localization/Messages';
import { PresentationMode } from '../../Enums/PresentationMode';
import { ServiceClient } from '../../Services/ServiceClient';
import { useDebouncedEffect } from '../../Helpers/CustomHooks';
import LoaderComponent from '../../Elements/ids/Loader';
import ErrorMessage from '../../Elements/ErrorMessage';
import { getQueryResultTitleText, isTeamQueryValid, searchTypes, shouldSearchAD } from './Functions';
import { useSearchParams } from 'react-router-dom';
import { useGlobalContext } from '../../context/GlobalContext';
import { createServerErrorMessage } from '../../Helpers/FormInputHelper';
import { Ui24Search } from '@ids/react-icons';
import { InputField, InputFieldAddon, InputFieldWrapper, InputSize } from '@ids/react-input-field';
import EmployerSearchResults from './Components/EmployerSearchResults';
import TeamSearchResults from './Components/TeamSearchResults';

const SearchPage = () => {
  const { resetBreadcrumb } = useGlobalContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('query') ?? '');
  const [results, setResults] = useState([]);
  const [pageMode, setPageMode] = useState(PresentationMode.View);
  const [error, setError] = useState(null);
  const [searchType, setSearchType] = useState(searchTypes.team);

  const handleError = error => {
    setResults([]);
    setPageMode(PresentationMode.Error);
    setError(createServerErrorMessage(error).message);
  };

  const searchAD = teams => {
    ServiceClient.getUserDetailsFromADbyUserID(
      query,
      adResult => {
        if (adResult) {
          const team = {
            name: adResult.displayName,
            userId: query.toUpperCase(),
            agents: [],
            isFromAD: true,
          };
          setResults([team, ...teams]);
        } else {
          setResults(teams);
        }
        setPageMode(PresentationMode.View);
      },
      error => {
        handleError(error);
      }
    );
  };

  const sendApiRequest = useCallback(() => {
    resetBreadcrumb();

    if (searchType === searchTypes.team) {
      if (!isTeamQueryValid(query)) {
        setResults([]);
        return;
      }

      setPageMode(PresentationMode.Loading);
      setSearchParams(searchParams => {
        searchParams.set('query', query);
        return searchParams;
      });

      ServiceClient.findTeams(
        query,
        teams => {
          if (teams.length === 0) {
            // if no teams have been found check that there are no inactive teams and then search AD
            ServiceClient.doesCodePrefixExist(
              query,
              doesTeamExist => {
                if (!doesTeamExist) {
                  searchAD(teams);
                } else {
                  setPageMode(PresentationMode.View);
                }
              },
              error => {
                handleError(error);
              }
            );
          } else {
            if (shouldSearchAD(query, teams)) {
              searchAD(teams);
            } else {
              setResults(teams);
              setPageMode(PresentationMode.View);
            }
          }
        },
        error => {
          handleError(error);
        }
      );
    } else if (query?.trim().length >= 1) {
      setPageMode(PresentationMode.Loading);
      ServiceClient.findEmployers(
        query,
        employers => {
          setResults(employers);
          setPageMode(PresentationMode.View);
        },
        error => {
          handleError(error);
        }
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useDebouncedEffect(sendApiRequest, 500);

  return (
    <>
      <div className="searchPage-container">
        <div className="searchPage-searchContainer">
          <div className="searchPage-searchContainer-waypointLogo"></div>
          <div className="searchPage-searchContainer-title">{formatMessage(Messages.searchPage_chooseCategory)}</div>
          <div className="searchPage-searchContainer-header">
            <div
              className={`clickable searchPage-searchContainer-headerItem ${
                searchType === searchTypes.team ? 'searchPage-searchContainer-headerItem-selected' : ''
              }`}
              onClick={() => {
                setSearchType(searchTypes.team);
                setQuery('');
                setResults([]);
                setSearchParams('');
              }}>
              <div>
                <img aria-hidden className="if size-24" src="/assets/people.svg" alt="Team" />
              </div>
              <div>{formatMessage(Messages.searchPage_headerTeam)}</div>
            </div>
            <div
              className={`clickable searchPage-searchContainer-headerItem ${
                searchType === searchTypes.employer ? 'searchPage-searchContainer-headerItem-selected' : ''
              }`}
              onClick={() => {
                setSearchType(searchTypes.employer);
                setQuery('');
                setResults([]);
                setSearchParams('');
              }}>
              <div>
                <img aria-hidden className="if size-24" src="/assets/worker24.svg" alt="Employer" />
              </div>
              <div>{formatMessage(Messages.searchPage_headerEmployer)}</div>
            </div>
          </div>
          <div className="searchPage-searchContainer-body">
            <div>
              {searchType === searchTypes.team
                ? formatMessage(Messages.searchPage_searchTitleTeam)
                : formatMessage(Messages.searchPage_searchTitleEmployer)}
            </div>
            <div className="searchPage-searchContainer-body-search">
              <InputFieldWrapper>
                <InputFieldAddon>
                  <Ui24Search aria-hidden />
                </InputFieldAddon>
                <InputField
                  inputSize={InputSize.MEDIUM}
                  onChange={e => {
                    setQuery(e.target.value);
                  }}
                  value={query}
                  autoComplete="off"
                />
              </InputFieldWrapper>
            </div>
          </div>
        </div>

        {pageMode === PresentationMode.Loading ? (
          <LoaderComponent />
        ) : pageMode === PresentationMode.Error ? (
          <ErrorMessage message={error} />
        ) : (
          <>
            {query.length > 0 && (
              <>
                {searchType === searchTypes.team ? (
                  <TeamSearchResults
                    results={results}
                    query={query}
                    resultSummary={getQueryResultTitleText(query, results.length)}
                  />
                ) : (
                  <EmployerSearchResults results={results} query={query} />
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SearchPage;
